<script setup lang="ts">
import { BriefcaseIcon, EnvelopeIcon, KeyIcon, PhoneIcon, UserCircleIcon, } from '@heroicons/vue/16/solid'
import {
  ExclamationTriangleIcon, EyeIcon, EyeSlashIcon,
  CheckCircleIcon, CheckIcon, ChevronUpDownIcon,
  XCircleIcon, PlusCircleIcon
} from '@heroicons/vue/20/solid'
import {
  Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions,
  Listbox, ListboxButton, ListboxOption, ListboxOptions,
  TransitionChild, TransitionRoot,
  Dialog, DialogPanel, ListboxLabel,
} from '@headlessui/vue'
import { useMutation, useLazyQuery } from '@vue/apollo-composable'
import { ErrorMessage, Field, Form, useForm } from 'vee-validate'
import { computed, onMounted, ref, useId, watch, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { cloneDeep, isNull, merge, omit } from 'lodash'
import * as yup from 'yup'
import router, { buildPathByErrorName, RoutePath } from '@/router.ts'
import { useRootStore } from '@/stores/root.store.ts'
import * as IUsers from '../users/interfaces.ts'
import * as IProjects from '../projects/interfaces.ts'
import { GetUserRoles, FindUsersAndRolesQuery, FindUsersQuery } from '@/components/users/graphql/user.queries.ts'
import { CreateOrUpdateUserMutation } from '@/components/users/graphql/user.mutations.ts'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/user.store.ts'
import { BackendErrorCode, ErrorName, UILanguage, UserRoleId } from '@/enums.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import type { ApolloQueryResult, MutationResult } from '@apollo/client'
import type { User } from '../users/interfaces.ts'
import { useToast } from 'vue-toastification'
import { ButtonType, ButtonVariant } from '@/components/common/crd-button/crd-button.enums.ts'
import CrdButton from '@/components/common/crd-button/CrdButton.vue'

enum FormField {
  Username = 'username',
  RoleId = 'roleId',
  Firstname = 'firstname',
  Lastname = 'lastname',
  ContactEmail = 'contactEmail',
  ContactPhone = 'contactPhone',
  CompanyName = 'companyName',
  Password = 'password',
  PasswordRepeat = 'passwordRepeat',
}

const { t } = useTranslations()
const { uiLanguages } = useRootStore()

function onCancelButtonClicked () {
  router.push({ path: RoutePath.Users })
}

function getDefaultFormState (): IUsers.User {
  if (5 > 6) {
    return {
      // @ts-expect-error  TODO: Fix later
      id: null as number,
      roleId: UserRoleId.ProjectOwner,
      username: 'eugene.cobolt',
      firstname: 'Eugene',
      lastname: 'Cobolt',
      password: 'bugaga123',
      passwordRepeat: 'bugaga123',
      contactEmail: 'john.doe4444@gmail.com',
      contactPhone: '',
      companyName: '',
      preferredLanguage: UILanguage.Estonian,
      projects: []
    }
  }

  return {
    // @ts-expect-error  TODO: Fix later
    id: null as number,
    roleId: UserRoleId.ProjectOwner,
    username: '',
    firstname: '',
    lastname: '',
    password: '',
    passwordRepeat: '',
    contactEmail: '',
    contactPhone: '',
    companyName: '',
    preferredLanguage: UILanguage.Estonian,
    projects: []
  }
}

// Stores
const route = useRoute()
const toast = useToast()
const userStore = useUserStore()
const FormId = {
  Id: useId(),
  RoleId: useId(),
  Username: useId(),
  Password: useId(),
  PasswordRepeat: useId(),
  Lastname: useId(),
  Firstname: useId(),
  CompanyName: useId(),
  ContactEmail: useId(),
  ContactPhone: useId(),
  PreferredLanguage: useId()
}
const { projects, userRoleId } = storeToRefs(userStore)
let roles = ref<IUsers.UserRole[]>([])
const query = ref('')
const selectedProjects = ref<IProjects.Project[]>([])
const isEditMode = ref<boolean>(route.path.endsWith('/edit'))
//const selectedRole = ref<null | IUsers.UserRole>(null)
const currentError = ref<null | BackendErrorCode>(null)
const projectsFiltered = computed(() => {
  return query.value === ''
    ? projects.value
    : projects.value.filter((project) => {
      return project.title.toLowerCase().includes(query.value.toLowerCase())
    })
})
const form = useForm<IUsers.User>({
  validationSchema: yup.object().shape({
    id: isEditMode.value
      ? yup.number().integer().required()
      : yup.number().integer().optional().nullable(),
    firstname: isEditMode.value // TODO: wtf condition revalidate
      ? yup
        .string()
        .trim()
        .min(3, t('common.validation.firstNameAtLeast', { min: 3 }))
        .max(32, t('common.validation.firstNameAtMost', { max: 32 }))
        .optional()
      : yup
        .string()
        .trim()
        .min(3, t('common.validation.firstNameAtLeast', { min: 3 }))
        .max(32, t('common.validation.firstNameAtMost', { max: 32 }))
        .required(t('common.validation.required')),
    lastname: yup
      .string()
      .trim()
      .min(3, t('common.validation.lastNameAtLeast', { min: 3 }))
      .max(32, t('common.validation.lastNameAtMost', { max: 32 }))
      .required(t('common.validation.required')),
    username: yup
      .string()
      .matches(/^[a-z]{3,}\.[a-z]{3,}$/, 'firstname.lastname')
      .required(t('common.validation.required')),
    contactEmail: yup
      .string()
      .required()
      .email(t('common.validation.emailIsInvalid'))
      .trim(),
    contactPhone: yup
      .string()
      .optional()
      .nullable()
      .min(0) // TODO: Bugfix behaviour
      .max(32, t('common.validation.maxLength', { max: 32 }))
      .trim(),
    companyName: yup
      .string()
      .optional()
      .nullable()
      .min(0) // TODO: Bugfix behaviour
      .max(32, t('common.validation.maxLength', { max: 32 }))
      .trim(),
    password: isEditMode.value
      ? yup.lazy((value) => {
        return (value ?? '').length > 0
          ? yup.string().ensure().trim()
            .min(8, t('common.validation.minLength', { min: 8 }))
            .max(32, t('common.validation.maxLength', { max: 32 }))
            .required(t('common.validation.required'))
          : yup.string().optional()
      })
      : yup.string().ensure().required(t('common.validation.required')),
    passwordRepeat: isEditMode.value
      ? yup
        .string()
        .oneOf([yup.ref('password')], t('common.validation.passwordsDoNotMatch'))
      : yup
        .string()
        .oneOf([yup.ref('password')], t('common.validation.passwordsDoNotMatch')),
  }),
  initialValues: getDefaultFormState()
})

const isProjectsSelectVisible = computed<boolean>(() => {
  return ![UserRoleId.SuperUser, UserRoleId.Administrator].includes(form.values.roleId as number)
})

const successMessage = ref<null | string>(null)
const showPassword = ref<boolean>(false)
const showPasswordRepeat = ref<boolean>(false)

// Queries and mutations: Definition
const {
  onDone: onUserCreateOrUpdateSucceed,
  onError: onUserCreateOrUpdateFailed,
  mutate: createOrUpdateUser
} = useMutation<{ user: User }>(CreateOrUpdateUserMutation)
const {
  onResult: onUserAndRolesLoadingSucceed,
  onError: onUserAndRolesLoadingFailed,
  load: loadUserAndRoles,
  loading: isLoading,
  error: loadingError
} = useLazyQuery(FindUsersAndRolesQuery, {
  input: {
    userId: parseInt(route.params.userId as string)
  }
})
const {
  onResult: onUserRolesLoadingSucceed,
  onError: onUserRolesLoadingFailed,
  load: loadUserRoles
} = useLazyQuery<{ userRoles: IUsers.UserRole[] }>(GetUserRoles)


// Queries and mutations: Events
// @ts-expect-error Jevgeni fix it later
onUserCreateOrUpdateSucceed(({ data: { user } }: MutationResult<{ user: User }>) => {
  const notificationText = t( isEditMode.value ? 'users.addOrEditModal.userUpdated' : 'users.addOrEditModal.userCreated')
  toast.success(notificationText)
  router.push({ path: RoutePath.Users })
})

onUserCreateOrUpdateFailed((err) => {
  if (Object.values(BackendErrorCode).includes(err.message as BackendErrorCode)) {
    setCurrentError(err.message as BackendErrorCode)
  } else {
    setCurrentError(err.message as BackendErrorCode)
  }
})

onUserAndRolesLoadingSucceed(({ data: { users, userRoles } }: ApolloQueryResult<{
  users: IUsers.User[],
  userRoles: IUsers.UserRole[]
}>) => {
  if (users.length !== 1) {
    router.push({
      path: buildPathByErrorName(ErrorName.UserNotFound)
    })

    return
  }

  const user = users[0] as IUsers.User
  const userProjectIds = (user.projects ?? []).map(({ id }) => id)

  form.setValues(user)
  form.meta.value.initialValues = user

  roles.value = userRoles
  // selectedRole.value = roles.value.find(({ id } ) => id === user.roleId) ?? null
  selectedProjects.value = projects.value.filter(({ id }) => userProjectIds.includes(id as number))
})

onUserAndRolesLoadingFailed((err) => {
  toast.error(err.message)
})

onUserRolesLoadingSucceed(({ data: { userRoles }}: ApolloQueryResult<{ userRoles: IUsers.UserRole[] }>) => {
  roles.value = userRoles
  // selectedRole.value = userRoles.find(({ id } ) => id === UserRoleId.ProjectOwner) ?? null
  /*
   if (isEmpty(data.value!.userRoles)) {
      console.error('empty roles allo')
      return
    }

    roles.value = data.value!.userRoles
    selectedRole.value = roles.value.find(({ id } ) => id === UserRoleId.ProjectOwner) ?? null
   */
})

onUserRolesLoadingFailed((err) => {
  console.error('onUserRolesLoadingFailed | Error:', onUserRolesLoadingFailed)
})

// TODO: Check interface twice
const onSubmit = form.handleSubmit((values, actions) => {
  const input = merge(omit(values, ['projects']), {
    projectIds: selectedProjects.value.map(({ id }) => id),
    companyName: values.companyName === '' ? null : values.companyName,
  })

  createOrUpdateUser({ input }, {
    // @ts-expect-error Jevgeni fix it later
    update<any>(proxy, { data: { user: gqlUser } }) {
      // Update user in users view
      // @ts-expect-error Jevgeni fix it later
      const { users } = proxy.readQuery<{ users: User[] }>({
        query: FindUsersQuery,
        variables: null,
      })

      proxy.writeQuery({
        query: FindUsersQuery,
        variables: null,
        data: {
          // @ts-expect-error Jevgeni fix it later
          users: isEditMode.value ? users.map((u) => gqlUser.id === u.id ? gqlUser : u) : users.concat(gqlUser),
        },
      })
    }
  })

}, ({ errors }) =>{
  console.error('AddOrEditUserModal | onInvalidSubmit:', errors)
  toast.error(`${JSON.stringify(errors, null, '\t')})`)
})



/* function onSubmit (values: unknown) {
  // @ts-expect-error TODO: Fix
  const formValues = omit(values, ['projects'])

  createOrUpdateUser({
    input: merge(omit(formValues, ['projects']), {
      projectIds: selectedProjects.value.map(({ id }) => id),
      roleId: selectedRole.value?.id
    })
  }, {
    // @ts-expect-error Jevgeni fix it later
    update<any>(proxy, { data: { user: gqlUser } }) {
      // Update user in users view
      // @ts-expect-error Jevgeni fix it later
      const { users } = proxy.readQuery<{ users: User[] }>({
        query: FindUsersQuery,
        variables: null,
      })

      proxy.writeQuery({
        query: FindUsersQuery,
        variables: null,
        data: {
          // @ts-expect-error Jevgeni fix it later
          users: isEditMode.value ? users.map((u) => gqlUser.id === u.id ? gqlUser : u) : users.concat(gqlUser),
        },
      })
    }
  })
} */

function setCurrentError (error: BackendErrorCode | null) {
  currentError.value = error
}

onMounted(async () => {
  if (isEditMode.value) {
    loadUserAndRoles()
  } else {
    loadUserRoles()
  }
})
</script>

<template>
  <div>
    <TransitionRoot as="template" :show="true">
      <Dialog class="relative z-10" >
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>
        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <form @submit="onSubmit">
                  <div v-if="successMessage" class="rounded-md bg-green-50 p-4 mb-5">
                    <div class="flex">
                      <div class="shrink-0">
                        <CheckCircleIcon class="h-5 w-5 text-green-400" aria-hidden="true" />
                      </div>
                      <div class="ml-3">
                        <p class="text-sm font-medium text-green-800">{{ successMessage }}</p>
                      </div>
                    </div>
                  </div>
                  <div v-if="currentError != null">
                    <div class="rounded-md bg-red-50 p-4 mb-5">
                      <div class="flex">
                        <div class="shrink-0">
                          <XCircleIcon class="size-5 text-red-400" aria-hidden="true" />
                        </div>
                        <div class="ml-3">
                          <h3 class="text-sm font-medium text-red-800">
                            {{ t(`BACKEND_ERROR.${currentError}`) }}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="space-y-2">
                    <div class="border-b border-gray-900/10 pb-6">
                      <div class="flex justify-between">
                        <h2 class="text-base/7 font-semibold text-gray-900">
                          {{ t(isEditMode ? 'users.addOrEditModal.editUser' : 'users.addOrEditModal.newUser') }}
                        </h2>
                        <div class="ml-5 text-xs text-red-500">
                          <ErrorMessage :name="FormField.Username" class="block"/>
                          <ErrorMessage :name="FormField.Firstname" class="block"/>
                          <ErrorMessage :name="FormField.Lastname" class="block"/>
                          <ErrorMessage :name="FormField.ContactEmail" class="block"/>
                          <ErrorMessage :name="FormField.ContactPhone" class="block"/>
                          <ErrorMessage :name="FormField.CompanyName" class="block"/>
                          <ErrorMessage :name="FormField.Password" class="block"/>
                          <ErrorMessage :name="FormField.PasswordRepeat" class="block"/>
                        </div>
                      </div>
                      <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div class="sm:col-span-3">
                          <label :for="FormId.Username" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.username') }}
                          </label>
                          <div class="mt-2 grid grid-cols-1">
                            <Field
                              type="text"
                              :disabled="isEditMode"
                              :name="FormField.Username"
                              :id="FormId.Username"
                              :class="{
                                'col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6': true,
                                'opacity-60': isEditMode,
                              }"
                            />
                            <UserCircleIcon class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4" aria-hidden="true" />
                          </div>
                        </div>
                        <div class="sm:col-span-3">
                          <label :for="FormId.RoleId" v-if="form.values.roleId && userRoleId < form.values?.roleId" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.systemRole') }}
                          </label>
                          <div class="mt-2 grid grid-cols-1">
                            <Field
                              v-slot="{ value: title }"
                              as="select"
                              :id="FormId.RoleId"
                              :name="FormField.RoleId"
                              v-if="form.values.roleId && userRoleId < form.values?.roleId"
                              class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                            >
                              <option v-for="{ title, id } in roles" :value="id" :key="id">{{ title }}</option>
                            </Field>
                          </div>
                        </div>
                      </div>
                      <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                        <div class="sm:col-span-3">
                          <label :for="FormId.Password" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.password') }}
                          </label>
                          <div class="mt-2 grid grid-cols-1">
                            <Field
                              :type="showPassword ? 'text' : 'password'"
                              :id="FormId.Password"
                              :name="FormField.Password"
                              class="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-10 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
                            />
                            <div class="pointer-events-none col-start-1 row-start-1 flex items-center justify-between">
                              <KeyIcon class="ml-3 size-5 text-gray-400 sm:size-4" aria-hidden="true" />
                              <button
                                type="button"
                                class="mr-3 pointer-events-auto"
                                @click="showPassword = !showPassword"
                              >
                                <component
                                  :is="showPassword ? EyeSlashIcon : EyeIcon"
                                  class="size-5 text-gray-400 hover:text-gray-500 sm:size-4"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div class="sm:col-span-3">
                          <label :for="FormId.PasswordRepeat" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.repeatPassword') }}
                          </label>
                          <div class="mt-2 grid grid-cols-1">
                            <Field
                              :type="showPasswordRepeat ? 'text' : 'password'"
                              :id="FormId.PasswordRepeat"
                              :name="FormField.PasswordRepeat"
                              class="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-10 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
                            />
                            <div class="pointer-events-none col-start-1 row-start-1 flex items-center justify-between">
                              <KeyIcon class="ml-3 size-5 text-gray-400 sm:size-4" aria-hidden="true" />
                              <button
                                type="button"
                                class="mr-3 pointer-events-auto"
                                @click="showPasswordRepeat = !showPasswordRepeat"
                              >
                                <component
                                  :is="showPasswordRepeat ? EyeSlashIcon : EyeIcon"
                                  class="size-5 text-gray-400 hover:text-gray-500 sm:size-4"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="border-b border-gray-900/10 pb-6">
                      <h2 class="text-base/6 font-light text-gray-900">
                        {{ t('users.addOrEditModal.personalData') }}
                      </h2>
                      <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                        <div class="sm:col-span-3">
                          <label :for="FormId.Firstname" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.firstName') }}
                          </label>
                          <div class="mt-2">
                            <Field
                              type="text"
                              :id="FormId.Firstname"
                              :name="FormField.Firstname"
                              class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                            />
                          </div>
                        </div>

                        <div class="sm:col-span-3">
                          <label :for="FormId.Lastname" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.lastName') }}
                          </label>
                          <div class="mt-2">
                            <Field
                              type="text"
                              :id="FormId.Lastname"
                              :name="FormField.Lastname"
                              class="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="border-b border-gray-900/10 pb-6">
                      <h2 class="text-base/7 font-light text-gray-900">
                        {{ t('users.addOrEditModal.contactData') }}
                      </h2>
                      <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                        <div class="sm:col-span-3">
                          <label :for="FormId.ContactPhone" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.phone') }}
                          </label>
                          <div class="mt-2 grid grid-cols-1">
                            <!-- TODO: Pattern maybe ? -->
                            <Field
                              type="tel"
                              :id="FormId.ContactPhone"
                              :name="FormField.ContactPhone"
                              class="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
                              placeholder="+372 5555 6666"
                            />
                            <PhoneIcon class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4" aria-hidden="true" />
                          </div>
                        </div>
                        <div class="sm:col-span-3">
                          <label :for="FormId.ContactEmail" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.emailAddress') }}
                          </label>
                          <div class="mt-2 grid grid-cols-1">
                            <Field
                              type="email"
                              :id="FormId.ContactEmail"
                              :name="FormField.ContactEmail"
                              placeholder="your@email.com"
                              class="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
                            />
                            <EnvelopeIcon class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4" aria-hidden="true" />
                          </div>
                        </div>
                      </div>
                      <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                        <div class="sm:col-span-3">
                          <label :for="FormId.CompanyName" class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.company') }}
                          </label>
                          <div class="mt-2 grid grid-cols-1">
                            <Field
                              type="text"
                              :id="FormId.CompanyName"
                              :name="FormField.CompanyName"
                              class="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
                              placeholder="MEES JA KOER OÜ"
                            />
                            <BriefcaseIcon class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4" aria-hidden="true" />
                          </div>
                        </div>
                        <div class="sm:col-span-3">
                          <label class="block text-sm/6 font-light text-gray-900">
                            {{ t('users.addOrEditModal.language') }}
                          </label>
                          <div class="mt-2 grid grid-cols-1">
                            <Field
                              v-slot="{ value }"
                              as="select"
                              name="preferredLanguage"
                              class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                            >
                              <option v-for="{ value, text } in uiLanguages" :value="value" :key="value">{{ text }}</option>
                            </Field>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="border-b border-gray-900/10 pb-6" v-if="isProjectsSelectVisible">
                      <h2 class="text-base/7 font-light text-gray-900">
                        {{ t('users.addOrEditModal.projectAccess') }} ({{ selectedProjects.length }})
                      </h2>
                      <span
                        v-if="selectedProjects.length === 0"
                        class="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10"
                      >
                        {{ t('users.addOrEditModal.pleaseChooseProjects') }}
                      </span>
                      <span
                        v-for="project in selectedProjects"
                        class="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10 mr-1"
                      >
                        {{ project.title }}
                      </span>
                      <div class="mt-2 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6">
                        <div class="sm:col-span-12">
                          <Combobox as="div" v-model="selectedProjects" @update:modelValue="query = ''" multiple>
                            <div class="relative mt-2">
                              <ComboboxInput
                                class="block w-full rounded-md bg-white py-1.5 pl-3 pr-12 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                                @change="query = $event.target.value"
                                @blur="query = ''"
                              />
                              <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                                <ChevronUpDownIcon class="size-5 text-gray-400" aria-hidden="true" />
                              </ComboboxButton>
                              <ComboboxOptions v-if="projectsFiltered.length > 0" class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                                <ComboboxOption v-for="project in projectsFiltered" :key="project.id as number" :value="project" as="template" v-slot="{ active, selected }">
                                  <li :class="['relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-indigo-600 text-white outline-none' : 'text-gray-900']">
                                    <div class="flex items-center">
                                      <span :class="['inline-block size-2 shrink-0 rounded-full', project.active ? 'bg-green-400' : 'bg-gray-200']" aria-hidden="true" />
                                      <span :class="['ml-3 truncate', selected && 'font-semibold']">
                                        {{ project.title }}
                                        <span class="sr-only"> is {{ project.active ? t('users.addOrEditModal.online') : t('users.addOrEditModal.offline') }}</span>
                                      </span>
                                    </div>
                                    <span v-if="selected" :class="['absolute inset-y-0 right-0 flex items-center pr-4', active ? 'text-white' : 'text-indigo-600']">
                                      <CheckIcon class="size-5" aria-hidden="true" />
                                      </span>
                                  </li>
                                </ComboboxOption>
                              </ComboboxOptions>
                            </div>
                          </Combobox>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="rounded-md bg-yellow-50 p-4"
                       v-if="isEditMode && form.values.password && (form.values.password ?? '').length > 8">
                    <div class="flex">
                      <div class="shrink-0">
                        <ExclamationTriangleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
                      </div>
                      <div class="ml-3">
                        <h3 class="text-sm font-medium text-yellow-800">
                          {{ t('users.addOrEditModal.passwordWillBeChanged') }}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <CrdButton
                      :variant="ButtonVariant.Save"
                      class="w-full justify-items-center outline"
                      :type="ButtonType.Submit"
                    >
                      <CheckCircleIcon v-if="isEditMode" class="block size-5" aria-hidden="true" />
                      <PlusCircleIcon v-else class="block size-5" aria-hidden="true"></PlusCircleIcon>

                      <span>{{ t(isEditMode ? 'common.buttons.save' : 'common.buttons.add') }}</span>
                    </CrdButton>
                    <CrdButton
                      :variant="ButtonVariant.Cancel"
                      @click="onCancelButtonClicked"
                    >
                      <XCircleIcon class="block size-5" aria-hidden="true" />
                      {{ t('common.buttons.cancel') }}
                    </CrdButton>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

